import { bandOf, matches, type MarketProduct, type PricePeriod, t } from '@procumi/shared';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { PriceBand } from '../components/prices/PriceBand';
import { Change, day, pct, price, RangeBar, Spark, Supplier } from '../components/prices/PriceMarks';
import { PriceVerdict } from '../components/prices/PriceVerdict';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import { api } from '../lib/api';
import type { Route } from '../routes';

/**
 * Prețurile „ca la bursă”: pe fiecare material, cel mai mic preț de la ultima
 * cerere, cine îl are și de la cine să comanzi. Crește singur din cereri — nu
 * se introduce nimic aici. Calculele sunt în `shared/market.ts`.
 */

const PERIODS: { id: PricePeriod; label: string; col: string; text: string }[] = [
  { id: '6', label: t('6 luni'), col: t('6 luni'), text: t('în ultimele 6 luni') },
  { id: '12', label: t('12 luni'), col: t('12 luni'), text: t('în ultimele 12 luni') },
  { id: 'tot', label: t('Tot'), col: t('toată perioada'), text: t('din tot istoricul') },
];

/** Începutul perioadei, ca pe server: azi minus N luni. */
function startOf(luni: PricePeriod): string | null {
  if (luni === 'tot') return null;
  const d = new Date();
  d.setMonth(d.getMonth() - Number(luni));
  return d.toISOString();
}

export function PricesPage({ go, material }: { go: (r: Route) => void; material?: string }) {
  const [luni, setLuni] = useState<PricePeriod>('12');
  const { data, error, loading, reload } = useAsync(() => api.prices(luni), [luni], `prices:${luni}`);
  const [cauta, setCauta] = useState('');
  const [open, setOpen] = useState<string | null>(material ?? null);
  const [flash, setFlash] = useState<string | null>(null);
  const [focus, setFocus] = useState<string | null>(material ?? null);

  const from = useMemo(() => startOf(luni), [luni]);
  const period = PERIODS.find((p) => p.id === luni)!;

  // „otel 12” găsește „Oțel beton PC52 Ø12”: fără diacritice, pe cuvinte
  const lista = useMemo(() => (data?.products ?? []).filter((p) => matches(p.desc, cauta)), [data, cauta]);
  const band = useMemo(() => bandOf(data?.products ?? [], from), [data, from]);

  // rândul cerut (din bandă sau din link) vine în fața ochilor după ce apare
  useEffect(() => {
    if (!focus || !data) return;
    document.getElementById(`pz-${focus}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlash(focus);
    setFocus(null);
  }, [focus, data]);
  // luminarea scurtă: cronometrul ține de ea, nu de efectul de mai sus, care
  // rulează din nou imediat (focus devine null) și l-ar opri
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 1300);
    return () => clearTimeout(t);
  }, [flash]);

  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox error={error ?? t('Nu am putut încărca.')} onRetry={reload} />;

  const slots = data.slots;
  const pick = (key: string) => {
    setCauta('');
    setOpen(key);
    setFocus(key);
  };

  const row = (p: MarketProduct) => {
    const on = open === p.key;
    return (
      <Fragment key={p.key}>
        <tr
          id={`pz-${p.key}`}
          className={`pz-row${on ? ' open' : ''}${flash === p.key ? ' flash' : ''}`}
          onClick={() => setOpen(on ? null : p.key)}
        >
          <td className="pz-mat">
            <b>{p.desc}</b>
            <span className="sub">
              {t(
                '{0} {1} · ultima pe {2}',
                p.requests,
                p.requests === 1 ? t('cerere') : t('cereri'),
                day(p.last.at),
              )}
            </span>
          </td>
          <td className="num">
            <span className="pz-now">{price(p.now)}</span> <span className="u">{t('lei/{0}', p.unit)}</span>
          </td>
          <td>
            <Change v={p.change} />
          </td>
          <td className="pz-wide">
            <Spark p={p} from={from} />
          </td>
          <td className="pz-wide">
            <RangeBar p={p} />
          </td>
          <td>
            <Supplier s={p.pick} slots={slots} />
            <span className="sub">
              {p.ties.length
                ? t('același preț: {0}', p.ties[0]!.name)
                : p.next
                  ? t('următorul: {0}, {1}', p.next.supplier.name, pct(p.next.price / p.now - 1))
                  : t('o singură ofertă')}
            </span>
          </td>
          <td className="pz-chev">
            <button
              type="button"
              aria-expanded={on}
              aria-label={on ? t('Închide {0}', p.desc) : t('Deschide {0}', p.desc)}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path
                  d="M2 4l4 4 4-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </td>
        </tr>
        {on ? (
          <tr className="pz-exp">
            <td colSpan={7}>
              <PriceVerdict p={p} slots={slots} periodText={period.text} go={go} />
            </td>
          </tr>
        ) : null}
      </Fragment>
    );
  };

  return (
    <>
      <div className="head">
        <div>
          <h1>{t('Prețuri')}</h1>
          <p>
            {t(
              'Cel mai mic preț primit la ultima cerere, pentru fiecare material. Se adună singur din oferte.',
            )}
          </p>
        </div>
        {data.products.length ? (
          <div className="pz-tools">
            <input
              className="inp"
              placeholder={t('Caută un material…')}
              value={cauta}
              onChange={(e) => setCauta(e.target.value)}
              aria-label={t('Caută un material')}
            />
            <div className="pz-seg" role="group" aria-label={t('Perioada')}>
              {PERIODS.map((x) => (
                <button
                  key={x.id}
                  type="button"
                  className={x.id === luni ? 'on' : ''}
                  aria-pressed={x.id === luni}
                  onClick={() => setLuni(x.id)}
                >
                  {x.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <PriceBand products={band} onPick={pick} />

      <section className="sheet pz">
        {lista.length ? (
          <div className="tbl-wrap">
            <table className="pz-tbl">
              <thead>
                <tr>
                  <th>{t('Material')}</th>
                  <th className="num">{t('Cel mai mic acum')}</th>
                  <th>{t('Față de data trecută')}</th>
                  <th className="pz-wide">
                    {luni === 'tot' ? t('Toată perioada') : t('Ultimele {0}', period.col)}
                  </th>
                  <th className="pz-wide">{t('Minim – maxim, {0}', period.col)}</th>
                  <th>{t('Cel mai ieftin acum')}</th>
                  <th aria-label={t('Deschide')} />
                </tr>
              </thead>
              <tbody>{lista.map(row)}</tbody>
            </table>
          </div>
        ) : (
          <div className="empty">
            {data.products.length ? (
              <>{t('Niciun material nu se potrivește cu „{0}".', cauta)}</>
            ) : (
              <>
                <b>{t('Încă nu ai prețuri în istoric.')}</b>
                <br />
                {t('Prima ofertă salvată pe o cerere intră aici, iar de la a doua vezi și evoluția.')}
              </>
            )}
          </div>
        )}
      </section>
      {lista.length ? (
        <p className="pz-note">
          {t(
            'Verde înseamnă că plătești mai puțin decât data trecută, roșu că plătești mai mult. Punctele din grafic sunt cererile: între ele nu știm prețul. Prețuri fără TVA.',
          )}
        </p>
      ) : null}
    </>
  );
}
